import * as z from "zod";
import {
  AdjustmentZodSchema,
  AllowedExchangeTypesZodSchema,
} from "../return-flow-schema";
import {
  ReturnShippingFeeSchema,
  type ReturnPolicySnapshotSettings,
} from "./snapshot-settings";

export const ReturnPolicySummarySchema = z.strictObject({
  returnWindowDays: z.number().int().nullable(),
  /** Null when exchanges are not offered by default. */
  exchangeTypes: AllowedExchangeTypesZodSchema.nullable(),
  exchangeBonus: AdjustmentZodSchema,
  refundsOffered: z.boolean(),
  returnShipping: ReturnShippingFeeSchema.shape.default,
  reimburseForFinalSale: z.boolean(),
  exceptionCount: z.number().int(),
  questionCount: z.number().int(),
});
export type ReturnPolicySummary = z.infer<typeof ReturnPolicySummarySchema>;

/** What a policy does by default, plus how many exceptions and questions it has. */
export function summarizeReturnPolicy(
  settings: ReturnPolicySnapshotSettings,
): ReturnPolicySummary {
  const methods = settings.return_methods_offered.default;
  return {
    returnWindowDays: settings.return_window.days,
    exchangeTypes: methods.includes("exchange")
      ? settings.exchange_types_offered.default
      : null,
    exchangeBonus: settings.item_adjustments_by_method.exchange.default,
    refundsOffered: methods.includes("refund"),
    returnShipping: settings.return_shipping_fee.default,
    reimburseForFinalSale: settings.reimburse_for_final_sale.default,
    exceptionCount: countExceptions(settings),
    questionCount: settings.questions.length,
  };
}

const RuleIdsSchema = z.array(
  z.object({ id: z.string(), setId: z.string().optional() }),
);
const ColumnRulesSchema = z.union([
  z.object({ rules: RuleIdsSchema }),
  RuleIdsSchema,
]);

/** Rules the merchant created together share a setId and count once. */
function countExceptions(settings: ReturnPolicySnapshotSettings): number {
  const exceptionIds = new Set<string>();
  for (const value of ruleColumnValues(settings)) {
    for (const rule of columnRules(value)) {
      exceptionIds.add(rule.setId ?? rule.id);
    }
  }
  return exceptionIds.size;
}

function ruleColumnValues(settings: ReturnPolicySnapshotSettings): unknown[] {
  const columns = Object.entries(settings)
    .filter(([column]) => column !== "questions")
    .map(([, value]) => value);
  return [...columns, ...Object.values(settings.item_adjustments_by_method)];
}

function columnRules(value: unknown): z.infer<typeof RuleIdsSchema> {
  const parsed = ColumnRulesSchema.safeParse(value);
  if (!parsed.success) {
    return [];
  }
  return Array.isArray(parsed.data) ? parsed.data : parsed.data.rules;
}
